/**
 * Check if debug bundle button should be visible
 * Returns true if any of:
 * - URL has ?debug=1
 * - localStorage has atlasDebug=1 (persisted debug mode)
 * - Mode is turbo (turbo users are power users who want debug access)
 * - Environment is not production
 *
 * @param searchParams - URL search params
 * @returns true if button should be visible
 */
export function shouldShowDebugBundle(searchParams: URLSearchParams): boolean {
  // Check URL query param
  if (searchParams.get('debug') === '1') return true;

  // Check localStorage flag
  try {
    if (typeof window !== 'undefined') {
      // Persisted debug mode
      if (localStorage.getItem('atlasDebug') === '1') {
        return true;
      }
      // Turbo mode users get debug access
      if (localStorage.getItem('hailmary.mode') === 'turbo') {
        return true;
      }
    }
  } catch {
    // localStorage may be unavailable in some contexts
  }

  // Non-production environments always have debug
  const env = getEnvironment();
  if (env !== 'prod') {
    return true;
  }

  return false;
}

/**
 * Enable debug bundle via localStorage
 */
export function enableDebugBundle(): void {
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem('atlasDebug', '1');
    }
  } catch {
    console.warn('Failed to enable debug bundle: localStorage unavailable');
  }
}

/**
 * Disable debug bundle via localStorage
 */
export function disableDebugBundle(): void {
  try {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('atlasDebug');
    }
  } catch {
    console.warn('Failed to disable debug bundle: localStorage unavailable');
  }
}

/**
 * Generate a simple hash from a string (for fingerprinting)
 * Not cryptographically secure, but deterministic for debugging purposes
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Create a deterministic fingerprint from normalized input
 * Order-independent hash of key fields
 */
export function createInputFingerprint(normalizedInput: unknown): string {
  if (!normalizedInput || typeof normalizedInput !== 'object') {
    return 'empty';
  }
  
  try {
    // Ensure it's a plain object with keys
    if (Array.isArray(normalizedInput)) {
      return simpleHash(JSON.stringify(normalizedInput));
    }
    
    // Sort keys to ensure deterministic output
    const sortedJson = JSON.stringify(normalizedInput, Object.keys(normalizedInput as Record<string, unknown>).sort());
    return simpleHash(sortedJson);
  } catch {
    return 'error';
  }
}

/**
 * Create a fingerprint from key output fields
 * Captures top option IDs and baseline to detect changes
 */
export function createOutputFingerprint(computeOutput: any): string {
  if (!computeOutput) {
    return 'empty';
  }
  
  try {
    const keyData = {
      primaryId: computeOutput.primary?.id,
      alternativeIds: computeOutput.alternatives?.map((a: any) => a.id).slice(0, 3),
      baselineCost: computeOutput.baselineResult?.totalCost_gbp,
    };
    
    const sortedJson = JSON.stringify(keyData, Object.keys(keyData).sort());
    return simpleHash(sortedJson);
  } catch {
    return 'error';
  }
}

/**
 * Get current environment (prod/staging/local)
 */
export function getEnvironment(): string {
  // Handle Node.js test environment
  if (typeof window === 'undefined') {
    return 'test';
  }
  
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  
  if (hostname.includes('staging') || hostname.includes('preview')) {
    return 'staging';
  }
  
  return 'prod';
}

/**
 * Generate a session ID (simple UUID v4)
 */
export function generateSessionId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Get or create a session ID for the current page load
 */
let currentSessionId: string | null = null;
export function getSessionId(): string {
  if (!currentSessionId) {
    currentSessionId = generateSessionId();
  }
  return currentSessionId;
}
